import React, { useEffect, useRef, useCallback } from "react";
import "../../../../../styles/documents/modals/new/new-modal.css";

export type ModalSize = "sm" | "md" | "lg" | "xl";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  disableCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  initialFocusSelector?: string;
  ariaLabelledById?: string;
  className?: string;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  disableCloseButton = false,
  closeOnBackdrop = true,
  closeOnEsc = true,
  initialFocusSelector,
  ariaLabelledById,
  className,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headerId = ariaLabelledById || "modal-title";

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (closeOnEsc && e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [open, closeOnEsc, onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (initialFocusSelector) {
      const el = dialog.querySelector<HTMLElement>(initialFocusSelector);
      if (el) {
        el.focus();
        return;
      }
    }
    const focusables = dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length) {
      focusables[0].focus();
    } else {
      dialog.focus();
    }
  }, [open, initialFocusSelector]);

  if (!open) return null;

  const onOverlayClick = (e: React.MouseEvent) => {
    if (!closeOnBackdrop) return;
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={onOverlayClick}
    >
      <div
        className={`modal ${sizeClass(size)} ${className || ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? headerId : undefined}
        ref={dialogRef}
        tabIndex={-1}
      >
        {(title || !disableCloseButton) && (
          <div className="modal__header">
            {title ? (
              <h2 className="modal__title" id={headerId}>
                {title}
              </h2>
            ) : (
              <div />
            )}
            {!disableCloseButton && (
              <button
                className="modal__iconbtn"
                aria-label="Close"
                onClick={onClose}
                type="button"
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div className="modal__divider" />

        <div className="modal__body">{children}</div>

        {footer !== undefined && (
          <>
            <div className="modal__divider" />
            <div className="modal__footer">{footer}</div>
          </>
        )}
      </div>
    </div>
  );
}

function sizeClass(size: ModalSize) {
  switch (size) {
    case "sm":
      return "modal--sm";
    case "md":
      return "modal--md";
    case "lg":
      return "modal--lg";
    case "xl":
      return "modal--xl";
    default:
      return "modal--md";
  }
}